<template>
  <ValidationObserver
    ref="customFieldObserver"
    v-slot="{ valid, pristine }" >
    <b-modal
      id="whatsapp-display-name-change-modal"
      ref="whatsappDisplayNameChangeModal"
      :modal-class="['settings-modal', {'change-pending': changePending}]"
      size="sm"
      no-close-on-backdrop
      centered
      hide-header-close
      @hidden="resetFields"
      @shown="focus" >
      <template #modal-header>
        <h5>Request official business account</h5>
      </template>

      <template v-if="changePending">
        <div class="flex column center">
          <span class="success-icon" />
          <h5>Your request for official business account was successfully submitted for a WhatsApp review</h5>

          <p>Once WhatsApp reviewed your request, you will receive a notification letting you know if your account has been granted official business account or not.</p>
          <p>
            Visit
            <a
              href="https://developers.facebook.com/docs/whatsapp/overview/business-accounts/#official-business-account"
              target="_blank">WhatsApp official business account guidelines</a>
            for more information.
          </p>
        </div>
      </template>

      <div v-else>
        <div class="prerequisites">
          <h3>Prerequisites</h3>
          <div class="p-item flex flex-align-center">
            <SBIcon
              class="inline vertical-middle"
              :icon="SBIconList.GREEN_TICK"
              color="#2680eb"
              :size="14" />
            <p>Business verification is complete.</p>
          </div>
          <div class="p-item flex flex-align-center">
            <SBIcon
              class="inline vertical-middle"
              :icon="SBIconList.GREEN_TICK"
              color="#2680eb"
              :size="14" />
            <p>Profile display name is approved.</p>
          </div>
          <div class="p-item flex flex-align-center">
            <SBIcon
              class="inline vertical-middle"
              :icon="SBIconList.GREEN_TICK"
              color="#2680eb"
              :size="14" />
            <p>Two-step verification is turned on.</p>
          </div>
        </div>

        <form @submit.prevent="requestOfficialAccount">
          <button
            class="hide"
            type="submit" />
          <ValidationProvider
            v-slot="{ errors, untouched }"
            name="website"
            rules="required|urlPrefixed" >
            <b-form-group>
              <label
                for="website"
                class="label flex flex-align-center" >
                Business website
              </label>

              <b-input
                ref="websiteInputRef"
                v-model="website"
                placeholder="https://your-website.com/"
                autocomplete="off"
                class="sb2-bordered sb2-hoverable sb2-focusable" />
              <div
                v-if="errors[0] && !untouched"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>

            </b-form-group>
          </ValidationProvider>

          <b-form-group class="no-margin-bottom" >
            <div class="label flex flex-align-center">
              <label
                class="settings__label"
                for="reason" >Reason for request</label>
              <SBIcon
                id="reason-popover"
                :icon="SBIconList.INFO"
                :color="SBColors.greyPrimary"
                :size="12"/>
              <span class="optional greedy text-right">Optional</span>
              <DotSeparator :size="3" class="ml-1 mr-1" />
              <span class="character-counter"> {{ reasonMax - reason.length }}</span>
            </div>
            <b-form-textarea
              id="reason"
              v-model="reason"
              name="reason"
              placeholder="Enter reason for request"
              class="sb2-bordered sb2-hoverable sb2-focusable"
              trim
              :maxlength="reasonMax"
              rows="3"
              max-rows="6" />
            <b-popover
              target="reason-popover"
              triggers="hover"
              placement="top"
              :delay="200" >
              Include supporting information here that might not be available on the links that you provide below.
            </b-popover>
          </b-form-group>

          <ValidationProvider
            v-slot="{ errors, untouched }"
            name="link 1"
            rules="urlPrefixed" >
            <b-form-group class="small-bottom">
              <div class="label flex flex-align-center">
                <label
                  class="settings__label"
                  for="reason" >Links</label>
                <SBIcon
                  id="links-popover"
                  :icon="SBIconList.INFO"
                  :color="SBColors.greyPrimary"
                  :size="12"/>
                <span class="optional greedy text-right">Optional</span>
                <b-popover
                  target="links-popover"
                  triggers="hover"
                  placement="top"
                  :delay="200" >
                  Add links to articles, social media accounts and other information that show your account is notable. Paid or promotional content will not be considered.
                </b-popover>
              </div>

              <b-input
                v-model="links.link1"
                placeholder="https://article1.com"
                autocomplete="off"
                class="sb2-bordered sb2-hoverable sb2-focusable" />
              <div
                v-if="errors[0] && !untouched"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>

            </b-form-group>
          </ValidationProvider>
          <ValidationProvider
            v-slot="{ errors, untouched }"
            name="link 2"
            rules="urlPrefixed" >
            <b-form-group class="small-top small-bottom">
              <b-input
                v-model="links.link2"
                placeholder="https://article2.com"
                autocomplete="off"
                class="sb2-bordered sb2-hoverable sb2-focusable" />
              <div
                v-if="errors[0] && !untouched"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>
            </b-form-group>
          </ValidationProvider>
          <ValidationProvider
            v-slot="{ errors, untouched }"
            name="link 3"
            rules="urlPrefixed" >
            <b-form-group class="small-top small-bottom">
              <b-input
                v-model="links.link3"
                placeholder="https://article3.com"
                autocomplete="off"
                class="sb2-bordered sb2-hoverable sb2-focusable" />
              <div
                v-if="errors[0] && !untouched"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>
            </b-form-group>
          </ValidationProvider>
          <ValidationProvider
            v-slot="{ errors, untouched }"
            name="link 4"
            rules="urlPrefixed" >
            <b-form-group class="small-top small-bottom">
              <b-input
                v-model="links.link4"
                placeholder="https://article4.com"
                autocomplete="off"
                class="sb2-bordered sb2-hoverable sb2-focusable" />
              <div
                v-if="errors[0] && !untouched"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>
            </b-form-group>
          </ValidationProvider>
          <ValidationProvider
            v-slot="{ errors, untouched }"
            name="link 5"
            rules="urlPrefixed" >
            <b-form-group class="small-top">
              <b-input
                v-model="links.link5"
                placeholder="https://article5.com"
                autocomplete="off"
                class="sb2-bordered sb2-hoverable sb2-focusable" />
              <div
                v-if="errors[0] && !untouched"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>
            </b-form-group>
          </ValidationProvider>

          <b-form-group>
            <div class="label flex flex-align-center">
              <label
                class="settings__label"
                for="reason" >Name in original language</label>
              <SBIcon
                id="lang-name-popover"
                :icon="SBIconList.INFO"
                :color="SBColors.greyPrimary"
                :size="12"/>
              <span class="optional greedy text-right">Optional</span>
            </div>
            <b-input
              v-model="originalLanguageName"
              placeholder="Enter name in original language"
              autocomplete="off"
              class="sb2-bordered sb2-hoverable sb2-focusable" />

            <b-popover
              target="lang-name-popover"
              triggers="hover"
              placement="top"
              :delay="200" >
              If the business name is in a language other than English, enter the name in its original language.
            </b-popover>
          </b-form-group>

          <ValidationProvider
            v-slot="{ errors, untouched }"
            name="Facebook page"
            rules="urlPrefixed" >
            <b-form-group>
              <div class="label flex flex-align-center">
                <label
                  class="settings__label"
                  for="reason" >Facebook page</label>
                <span class="optional greedy text-right">Optional</span>
              </div>

              <b-input
                v-model="facebookPage"
                placeholder="https://facebook.com/your_page_name"
                autocomplete="off"
                class="sb2-bordered sb2-hoverable sb2-focusable" />
              <div
                v-if="errors[0] && !untouched"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>

            </b-form-group>
          </ValidationProvider>
        </form>

        <div
          v-if="requestError"
          class="error flex flex-align-center">
          <SBIcon
            class="mr-2"
            :icon="SBIconList.ERROR"
            :color="SBColors.redPrimary"
            :size="12" />
          <span>Something went wrong. Please try again later.</span>
        </div>
      </div>

      <template
        slot="modal-footer">
        <b-button
          @click="hide">
          Close
        </b-button>
        <b-button
          v-if="!changePending"
          variant="primary"
          :disabled="saving || !valid || pristine"
          :class="{loading: saving}"
          class="flex settings__actions--save"
          @click="requestOfficialAccount()">
          <span>Save</span>
          <Spinner
            v-if="saving"
            :size="14"
            :thickness="2"
            :color="SBColors.white" />
        </b-button>
      </template>

    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapGetters } from 'vuex';

import Spinner from '@/components/common/Spinner';
import SBColors from '@/helpers/lists/colors';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBIcon from '@/components/common/Icon';
import api from '@/helpers/api';
import DotSeparator from '@/components/common/DotSeparator';

export default {
  name: 'WhatsAppOfficialAccountModal',
  components: {
    Spinner,
    SBIcon,
    DotSeparator,
  },
  props: {
    business: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {
      saving: false,
      reasonMax: 512,
      changePending: false,
      requestError: false,
      website: '',
      reason: '',
      links: {
        link1: '',
        link2: '',
        link3: '',
        link4: '',
        link5: '',
      },
      originalLanguageName: '',
      facebookPage: '',
      SBColors,
      SBIconList,
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
    }),
  },
  watch: {},
  methods: {
    requestOfficialAccount() {
      this.$refs.customFieldObserver.validate()
        .then((valid) => {
          if (!valid) {
            return;
          }
          this.saving = true;
          const data = {
            business_website: this.website,
            link1: this.links.link1,
            link2: this.links.link2,
            link3: this.links.link3,
            link4: this.links.link4,
            link5: this.links.link5,
            original_name: this.originalLanguageName,
            facebook_page: this.facebookPage,
            reason: this.reason,
          };
          api('POST', `organisation/${this.currentOrganisation.id}/business/${this.business.id}/whatsapp/green-badge-request`, data, null, 'w')
            .then((response) => {
              if (response.status === 200) {
                this.changePending = true;
                this.$emit('official-requested', response.data);
              }
            })
            .catch(() => {
              this.requestError = true;
            })
            .finally(() => {
              this.saving = false;
            });
        });
    },
    show() {
      this.$refs.whatsappDisplayNameChangeModal.show();
    },
    hide() {
      this.$refs.whatsappDisplayNameChangeModal.hide();
    },
    focus() {
      if (this.$refs.websiteInputRef) {
        this.$refs.websiteInputRef.focus();
      }
    },
    resetFields() {
      this.saving = false;
      this.changePending = false;
      this.requestError = false;

      this.website = '';
      this.reason = '';
      this.links = {
        link1: '',
        link2: '',
        link3: '',
        link4: '',
        link5: '',
      };
      this.originalLanguageName = '';
      this.facebookPage = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.top-margin-20 {
  margin-top: 20px;
}

.form-group .optional, .character-counter {
  color: $greyPrimary;
}

.prerequisites {
  margin: 0 0 15px;
  padding: 15px 14px;
  border-radius: 5px;
  border: solid 1px $greyBorder;
  background-color: #f0f8ff;

  h3 {
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 17px;
  }

  .p-item {
    margin-top: 10px;
  }

  .sb-icon {
    margin-right: 6px;
  }
}

textarea {
  overflow-y: auto !important;
  padding: 8px 10px !important;
}

.form-group {
  margin: 20px 0 0 0;

  &.small-top {
    margin-top: 10px !important;
  }
  &.small-bottom {
    margin-bottom: 10px !important;
  }

  label {
    margin: 0 !important;
  }

  .label {
    margin-bottom: 6px !important;

    ::v-deep .sb-icon {
      margin-left: 6px;
    }
  }
}
</style>

<style lang="scss">
.settings-modal.change-pending {
  .modal-header {
    border-bottom: none !important;

    h5 {
      display: none;
    }
  }

  .modal-body {
    background-color: $white !important;
    padding: 14px 48px 60px !important;

    .success-icon {
      margin-bottom: 15px;
      width: 100%;
      height: 70px;
      background-image: url('~@/assets/img/SB-Icons/whatsapp-submission.svg');
      background-position: center!important;
      background-repeat: no-repeat !important;
      background-size: contain !important;
    }

    h5 {
      font-size: 16px;
      font-weight: 500;
    }

    p {
      font-size: 14px;
      font-weight: 400;
      color: $blackUltralight;
      margin-top: 18px;

      a {
        color: inherit;
        text-decoration: underline;

        &:hover {
          color: $blackPrimary;
        }
      }

      .pill {
        padding: 1px 7px 2px;
        border-radius: 3px;
        border: solid 1px #e5e9eb;
        background-color: #edf0f2;
        font-size: 9px;
        font-weight: 500;
        color: $blackPrimary;
      }
    }
  }
}</style>
